// =============================================================================
// RoutineDraftSpec — the shared contract of the low-friction onboarding seam.
//
// Three flows author draft specs: domain-first inference (an LLM reading web
// search snippets), the web-call close (the voice agent's interview tool), and
// the phone onboarding interview. All three are UNTRUSTED authors: an LLM can
// hallucinate slugs, a tool call can arrive malformed, a search snippet can
// attempt prompt injection. House rule (lib/routines/schedule-parser.ts): the
// LLM is the parser, the VALIDATOR is deterministic — sanitizeDraftSpec() is
// that validator, and it runs BEFORE any spec is persisted. A spec read back
// from onboarding_drafts is trusted precisely because nothing unsanitized is
// ever written there.
//
// Pure module: no I/O, no Supabase — unit-testable in isolation. The service
// that persists specs lives in lib/services/onboarding-draft-service.ts.
// =============================================================================

import type { RoutineQuestion } from '@/lib/services/routine-template-service';
import { sanitizeChannelIntent, type ChannelIntent } from '@/lib/campaign';
import { QUESTIONS_MAX, QUESTION_TEXT_MAX, FOLLOW_UP_MAX } from '@/lib/routines/question-edit';
import { isValidCron } from '@/lib/routines/cron';
import { isCanonicalTimeZone } from '@/lib/routines/timezone';
import { screenReadAloudText } from '@/lib/onboarding/content-screen';

export type DraftSource = 'domain' | 'voice_call' | 'phone_call';

export interface RoutineDraftSpec {
  version: 1;
  /** Must exist in routine_templates — enforced against the live slug list. */
  templateSlug: string;
  name?: string;
  description?: string;
  purpose?: string;
  /** 1..MAX_QUESTIONS items, orders renumbered 1..n. */
  questions?: RoutineQuestion[];
  /** The visitor's cadence words VERBATIM ("every Friday at 9am") — parsed
   *  deterministically by resolveScheduleCron at claim time, never by the
   *  authoring LLM (house rule: the LLM is the parser's INPUT, not the parser). */
  scheduleNatural?: string;
  /** Resolved at draft time (lib/onboarding/draft-schedule.ts) from the
   *  template default or ONE parseNaturalToCron call — what the card says and
   *  what the launch creates the routine on. Kept only when isValidCron. */
  scheduleCron?: string;
  /** IANA zone the cron is read in. Kept only when isCanonicalTimeZone. */
  timezone?: string;
  channelIntent?: ChannelIntent;
  /** Display-only chips on the wizard banner. */
  industry?: string;
  teamType?: string;
  /** Lets copy read "Acme's Monday report". */
  companyName?: string;
  /** Flow C: pre-seeds the wizard's email gate. */
  ownerEmail?: string;
  /** First names/roles the visitor mentioned in the interview — pre-seeds the
   *  wizard's invite rows as DISPLAY ONLY. Never contact details: collecting a
   *  third party's phone/email in-call is the interviewer's hard rule. */
  inviteNames?: string[];
  source: DraftSource;
  sourceMeta?: {
    domain?: string;
    roomName?: string;
    leadId?: string;
    visitorName?: string;
  };
}

// Field caps. Deliberately tight: everything here renders in wizard inputs and
// marketing surfaces; a runaway LLM answer must clamp, not flow.
const MAX_NAME = 80;
const MAX_DESCRIPTION = 200;
const MAX_PURPOSE = 300;
// Question caps are shared with the live-edit engine (R4-1a): one source of
// truth for authoring AND editing, so the two paths can never drift.
const MAX_QUESTION = QUESTION_TEXT_MAX;
const MAX_FOLLOW_UP = FOLLOW_UP_MAX;
const MAX_QUESTIONS = QUESTIONS_MAX;
const MAX_CHIP = 60; // industry / teamType
const MAX_COMPANY = 80;
const MAX_EMAIL = 254;
const MAX_META = 200;
const MAX_INVITE_NAMES = 12;
const MAX_INVITE_NAME = 60;

const DRAFT_SOURCES: readonly DraftSource[] = ['domain', 'voice_call', 'phone_call'];

// Same shape the wizard's own email input accepts — deliberately loose; the
// real validation happens when Supabase attaches the address.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function clampString(raw: unknown, max: number): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  return trimmed.length > max ? trimmed.slice(0, max).trimEnd() : trimmed;
}

/**
 * Deterministic validator for anything claiming to be a RoutineDraftSpec.
 *
 * Returns null ONLY when the spec is unusable (unknown/missing templateSlug or
 * source) — every other defect is repaired: fields are clamped, malformed
 * questions are dropped individually, orders are renumbered, unknown keys are
 * discarded by construction (we copy field-by-field, never spread).
 */
export function sanitizeDraftSpec(
  raw: unknown,
  allowedSlugs: string[],
): RoutineDraftSpec | null {
  if (!raw || typeof raw !== 'object') return null;
  const input = raw as Record<string, unknown>;

  const templateSlug = clampString(input.templateSlug, 100);
  if (!templateSlug || !allowedSlugs.includes(templateSlug)) return null;

  const source = DRAFT_SOURCES.includes(input.source as DraftSource)
    ? (input.source as DraftSource)
    : null;
  if (!source) return null;

  const spec: RoutineDraftSpec = { version: 1, templateSlug, source };

  // Name and questions are read aloud to third parties by Tessa: a link, a
  // number, or credential/payment vocabulary is dropped here, not softened.
  // Control characters are NOT screened here (the default): this sanitizer
  // is shared by the interview, voice and domain create paths, none of them
  // flag-gated, and a name with a tab or a question with a line break
  // persists exactly as it always has. The thread's edit path rejects them
  // upstream, in lib/routines/diff.ts.
  const name = clampString(input.name, MAX_NAME);
  if (name && screenReadAloudText(name) === 'ok') spec.name = name;
  const description = clampString(input.description, MAX_DESCRIPTION);
  if (description) spec.description = description;
  const purpose = clampString(input.purpose, MAX_PURPOSE);
  if (purpose) spec.purpose = purpose;

  if (Array.isArray(input.questions)) {
    const cleaned: RoutineQuestion[] = [];
    for (const q of input.questions) {
      if (cleaned.length >= MAX_QUESTIONS) break;
      if (!q || typeof q !== 'object') continue;
      const question = clampString((q as Record<string, unknown>).question, MAX_QUESTION);
      if (!question || screenReadAloudText(question) !== 'ok') continue;
      const followUp = clampString(
        (q as Record<string, unknown>).follow_up_prompt, MAX_FOLLOW_UP,
      );
      cleaned.push({
        order: cleaned.length + 1, // renumber — author-provided orders are noise
        question,
        ...(followUp ? { follow_up_prompt: followUp } : {}),
      });
    }
    if (cleaned.length > 0) spec.questions = cleaned;
  }

  const scheduleNatural = clampString(input.scheduleNatural, 120);
  if (scheduleNatural) spec.scheduleNatural = scheduleNatural;
  const scheduleCron = clampString(input.scheduleCron, 64);
  if (scheduleCron && isValidCron(scheduleCron)) spec.scheduleCron = scheduleCron;
  const timezone = clampString(input.timezone, 64);
  if (timezone && isCanonicalTimeZone(timezone)) spec.timezone = timezone;

  const intent = sanitizeChannelIntent(
    typeof input.channelIntent === 'string' ? input.channelIntent : null,
  );
  if (intent) spec.channelIntent = intent;

  const industry = clampString(input.industry, MAX_CHIP);
  if (industry) spec.industry = industry;
  const teamType = clampString(input.teamType, MAX_CHIP);
  if (teamType) spec.teamType = teamType;
  const companyName = clampString(input.companyName, MAX_COMPANY);
  if (companyName) spec.companyName = companyName;

  const ownerEmail = clampString(input.ownerEmail, MAX_EMAIL);
  if (ownerEmail && EMAIL_RE.test(ownerEmail)) spec.ownerEmail = ownerEmail.toLowerCase();

  if (Array.isArray(input.inviteNames)) {
    const seen = new Set<string>();
    const names: string[] = [];
    for (const raw of input.inviteNames) {
      if (names.length >= MAX_INVITE_NAMES) break;
      const cleaned = clampString(raw, MAX_INVITE_NAME);
      if (!cleaned) continue;
      const dedupeKey = cleaned.toLowerCase();
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      names.push(cleaned);
    }
    if (names.length > 0) spec.inviteNames = names;
  }

  if (input.sourceMeta && typeof input.sourceMeta === 'object') {
    const rawMeta = input.sourceMeta as Record<string, unknown>;
    const meta: NonNullable<RoutineDraftSpec['sourceMeta']> = {};
    const domain = clampString(rawMeta.domain, MAX_META);
    if (domain) meta.domain = domain.toLowerCase();
    const roomName = clampString(rawMeta.roomName, MAX_META);
    if (roomName) meta.roomName = roomName;
    const leadId = clampString(rawMeta.leadId, MAX_META);
    if (leadId) meta.leadId = leadId;
    const visitorName = clampString(rawMeta.visitorName, MAX_META);
    if (visitorName) meta.visitorName = visitorName;
    if (Object.keys(meta).length > 0) spec.sourceMeta = meta;
  }

  return spec;
}

/**
 * The rhythm a stored draft hands the public wizard (the LINK path from the
 * thread and the ?draft= link from the claim email). Everything the visitor
 * was told was saved — "Saved: When: Mondays 8:00 am, Vancouver time" — must
 * be what the wizard opens on; the template default + the browser's zone
 * would silently undo it (and an owner who launched from Vancouver would get
 * the crew rung in another zone). Only the fields the spec carries are
 * returned, so a spec without a cadence keeps the template default. The spec
 * is trusted here because sanitizeDraftSpec already validated cron and zone.
 */
export function wizardScheduleSeed(
  spec: Pick<RoutineDraftSpec, 'scheduleCron' | 'scheduleNatural' | 'timezone'>,
): { scheduleCron?: string; scheduleNatural?: string; timezone?: string } {
  const seed: { scheduleCron?: string; scheduleNatural?: string; timezone?: string } = {};
  if (typeof spec.scheduleCron === 'string' && isValidCron(spec.scheduleCron)) {
    seed.scheduleCron = spec.scheduleCron;
  }
  if (typeof spec.scheduleNatural === 'string' && spec.scheduleNatural.trim()) {
    seed.scheduleNatural = spec.scheduleNatural;
  }
  if (typeof spec.timezone === 'string' && isCanonicalTimeZone(spec.timezone)) {
    seed.timezone = spec.timezone;
  }
  return seed;
}

/**
 * Derive a report-section title from a drafted question, for the deterministic
 * sample-report preview (zero LLM — a generated fake report with invented
 * specificity would read as a lie on the first touch).
 *
 * "What's blocking you this week — access, scope, or the client team?"
 *   → "Blocking you this week"
 */
export function deriveSectionTitle(question: string): string {
  let title = question.trim();
  // Cut at the first clause boundary — em dash, colon, comma, or "?".
  const boundary = title.search(/[—:?]|, /);
  if (boundary > 0) title = title.slice(0, boundary);
  // Drop leading interrogatives/fillers so the fragment reads like a heading.
  title = title
    .replace(
      /^(what's|what is|what are|what|how's|how is|how are|how|who's|who is|who|is there|are there|any|do you|did you|tell me about|del?)\s+/i,
      '',
    )
    .trim();
  if (!title) title = question.trim().replace(/\?+$/, '');
  // Sentence-case the fragment and keep it heading-sized.
  title = title.charAt(0).toUpperCase() + title.slice(1);
  if (title.length > 48) title = `${title.slice(0, 48).trimEnd()}…`;
  return title.replace(/\?+$/, '');
}
